import { ALLOWED_PRICE_STEPS_PER_PTU } from '@/features/bidding/constants'
import type { BidValidationResult, ValidationError } from '@/features/bidding/types/bidOfferPriceFlow'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import type { AggregatedBid } from '@/features/bidding/utils/model/buildAggregatedBid'
import type { AggregatedBidWrapper } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'

import { validateAggregatedBidCore } from './aggregatedBidValidations'

/**
 * Validates an aggregated bid (used in aggregated flow)
 *
 * Simple validation that checks if any PTU has negative remaining volume,
 * which would indicate overbidding.
 *
 * @param aggregatedBid - The aggregated bid to validate
 * @returns Validation result with isValid flag and optional errors
 */
export function validateAggregatedFlow(aggregatedBid: AggregatedBid): BidValidationResult {
  const errors: ValidationError[] = []

  // Check for overbidding (negative remaining volume)
  for (const ptu of aggregatedBid.ptus) {
    if (ptu.remaining < 0) {
      errors.push({
        ptuStart: ptu.ptuStart,
        type: 'overbidding',
        message: `PTU ${ptu.ptuStart.toFormat('HH:mm')} has negative remaining volume (${ptu.remaining})`,
      })
    }
  }

  return {
    isValid: errors.length === 0,
    errors: errors.length > 0 ? errors : undefined,
  }
}

/**
 * Validates bids per activation group (used in by-activation-group flow)
 *
 * Uses the core validation logic to check each activation group's bid for:
 * - At least one price chunk
 * - No overbidding (negative remaining)
 * - No underbidding (positive remaining)
 * - Single-price-step partner rules (max price steps per PTU)
 * - Price-area-aggregated partner rules (one shared price per PTU across activation groups)
 *
 * Builds detailed error messages for form validation and submit button state.
 *
 * @param bidsPerActivationGroup - Map of activation group UUID to aggregated bid wrapper
 * @param groupParams - The grouped bid parameters (deliveryDay, marketProgram, portfolioCode, bidType)
 * @returns Validation result with isValid flag and optional detailed errors
 */
export function validateByActivationGroupFlow(
  bidsPerActivationGroup: Map<string, AggregatedBidWrapper>,
  groupParams: GroupedBidParams,
): BidValidationResult {
  const errors: ValidationError[] = []

  for (const [activationGroupUuid, wrapper] of bidsPerActivationGroup.entries()) {
    const { aggregatedBid, activationGroup } = wrapper

    // Use core validation logic (single source of truth)
    const flags = validateAggregatedBidCore(aggregatedBid, activationGroup, bidsPerActivationGroup, groupParams.bidType)

    // Check if at least one chunk exists
    if (!flags.hasChunks) {
      errors.push({
        activationGroupUuid,
        type: 'no_chunks',
        message: `Activation group ${activationGroup.code} has no price chunks`,
      })
      // Skip other validations if no chunks exist
      continue
    }

    // Build detailed errors for overbidding PTUs
    for (const ptu of flags.overbiddingPtus) {
      errors.push({
        ptuStart: ptu.ptuStart,
        activationGroupUuid,
        type: 'overbidding',
        message: `Activation group ${activationGroup.code} PTU ${ptu.ptuStart.toFormat('HH:mm')} has negative remaining volume (${ptu.remaining})`,
      })
    }

    // Build detailed errors for underbidding PTUs
    for (const ptu of flags.underbiddingPtus) {
      errors.push({
        ptuStart: ptu.ptuStart,
        activationGroupUuid,
        type: 'underbidding',
        message: `Activation group ${activationGroup.code} PTU ${ptu.ptuStart.toFormat('HH:mm')} has positive remaining volume (${ptu.remaining})`,
      })
    }

    for (const ptu of flags.priceStepViolationPtus) {
      const nonZeroPriceQuantities = ptu.priceQuantities.filter((pq) => pq.quantity !== 0)
      errors.push({
        ptuStart: ptu.ptuStart,
        activationGroupUuid,
        type: 'max_price_steps',
        message: `Activation group ${activationGroup.code} PTU ${ptu.ptuStart.toFormat('HH:mm')} has ${nonZeroPriceQuantities.length} price steps (max ${ALLOWED_PRICE_STEPS_PER_PTU} allowed for this partner)`,
      })
    }

    for (const ptu of flags.crossActivationGroupPriceViolationPtus) {
      errors.push({
        ptuStart: ptu.ptuStart,
        activationGroupUuid,
        type: 'cross_activation_group_price',
        message: `Activation group ${activationGroup.code} PTU ${ptu.ptuStart.toFormat('HH:mm')} is priced differently from the other activation groups (this partner's bids are aggregated per price area, so all activation groups must share one price)`,
      })
    }
  }

  return {
    isValid: errors.length === 0,
    errors: errors.length > 0 ? errors : undefined,
  }
}
